import { useRef } from 'react';
import { useModals, useSnackbars } from '@customerio/pluma-components/react';
import { getUserFacingErrorMessage } from '@/services/api/errors';
import { useUpdateSqlImport } from '@/services/db/sql-import/mutations';
import type { SqlDatabaseType } from '@/services/db/sql-import-database/entity';
import type { SqlImport } from '@/services/db/sql-import/entity';
import { SETTINGS_FORM_ID } from '../../new/components/settings-step';
import type { DatabaseStepState } from '../../new/use-database-step';
import { settingsNeedQueryReview, settingsWriteBody } from '../edit';
import type { DraftEdits } from '../use-draft-edits';
import { QueryReviewModal } from '../components/query-review-modal';
import { RUN_QUERY_PARAM } from './query-tab';

const SAVE_FAILED_MESSAGE = "The settings couldn't be saved.";
const INVALID_MESSAGE = 'Fix the errors before saving.';

type Deferred = { resolve: () => void; reject: (reason: Error) => void };

type UseSettingsSaveArgs = {
  workspaceId: number;
  type: SqlDatabaseType;
  sqlImportId: string;
  sqlImport: SqlImport;
  edits: DraftEdits;
  databaseStep: DatabaseStepState;
  /** The Query tab, for the query review modal's "Test query". */
  queryHref: string;
};

/**
 * The Settings tab's save: the whole import goes out, the draft re-baselines, and the customer is
 * told either that the settings are saved or, when the identifier or database moved, that the
 * query may need to follow. Also the leave prompt's "Save and continue", which submits the form
 * by id so its rules run, and waits here for the outcome.
 */
export function useSettingsSave({
  workspaceId,
  type,
  sqlImportId,
  sqlImport,
  edits,
  databaseStep,
  queryHref,
}: UseSettingsSaveArgs) {
  const modals = useModals();
  const snackbars = useSnackbars();
  const update = useUpdateSqlImport({ workspaceId });
  const inFlight = useRef(false);
  const pendingLeave = useRef<Deferred | null>(null);

  const openQueryReview = () => {
    modals.open(QueryReviewModal, {
      name: sqlImport.name,
      databaseName: databaseStep.selected?.name ?? '',
      peoplePlural: 'people',
      testQueryHref: `${queryHref}?${RUN_QUERY_PARAM}=true`,
    });
  };

  /** The form validated: send it. Resolves to whether the save landed. */
  const save = async (): Promise<boolean> => {
    if (inFlight.current) {
      return false;
    }
    inFlight.current = true;
    const sent = edits.draft;
    const needsQueryReview = settingsNeedQueryReview(edits.baseline, sent);
    try {
      await update.mutateAsync({ sqlImportId, ...settingsWriteBody(sqlImport, sent, type) });
      edits.markSaved(sent);
      if (needsQueryReview) {
        openQueryReview();
      } else {
        snackbars.create({ id: 'sqlImports.settings.updated', message: 'Settings updated' });
      }
      return true;
    } catch (error) {
      snackbars.create({
        id: 'sqlImports.settings.error',
        variant: 'critical',
        message: getUserFacingErrorMessage(error, SAVE_FAILED_MESSAGE),
      });
      return false;
    } finally {
      inFlight.current = false;
    }
  };

  const settleLeave = (saved: boolean, reason: string) => {
    const leave = pendingLeave.current;
    pendingLeave.current = null;
    if (leave == null) {
      return;
    }
    if (saved) {
      leave.resolve();
    } else {
      leave.reject(new Error(reason));
    }
  };

  /** The prompt's save: submit the form by id, so the rules run, and wait for the save. */
  const saveAndContinue = () =>
    new Promise<void>((resolve, reject) => {
      pendingLeave.current = { resolve, reject };
      const form = document.getElementById(SETTINGS_FORM_ID);
      if (form instanceof HTMLFormElement) {
        form.requestSubmit();
      } else {
        settleLeave(false, SAVE_FAILED_MESSAGE);
      }
    });

  return {
    isSaving: update.isPending,
    saveAndContinue,
    /** The form validated. */
    onSubmit: () => void save().then((saved) => settleLeave(saved, SAVE_FAILED_MESSAGE)),
    /** The form was refused; a pending leave stays on the page. */
    onInvalid: () => settleLeave(false, INVALID_MESSAGE),
  };
}
